import React from 'react';
import Icon from '../app_components/Icon';
import StandardButton from '../app_components/StandardButton';
import SettingsLongTextInput from '../form_sections/SettingsLongTextInput';
import DropdownSelect from '../form_sections/DropdownSelect';
import DateInput from '../form_sections/DateInput';
import LexChipGroup from '../form_sections/LexChipGroup';
import SelectButton from '../form_sections/SelectButton';
import OnboardingToast, { bindOnboardingToastHandlers } from './OnboardingToast';
import { semantic } from '@lexody/design-tokens';
import { getMaxBirthdayDate, isAtLeastMinAge } from '../../utils/birthdayHelpers';

class SignUpCompleteProfile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      user: {
        bio: '',
        nationality: '',
        gender_id: null,
        birthday: null,
        local_chats_only: false,
        first_name: '',
      },
      allGenders: [],
      allNationalities: [],
      loaded: false,
      submitting: false,
      errors: {},
      toastMessage: null,
    };

    bindOnboardingToastHandlers(this);
    this.formRef = React.createRef();
    this.updateUser = this.updateUser.bind(this);
    this.updateBirthday = this.updateBirthday.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    Promise.all([
      $.ajax({ url: '/get_current_user', dataType: 'json', type: 'GET' }),
      $.ajax({ url: '/get_all_genders', dataType: 'json', type: 'GET' }),
      $.ajax({ url: '/get_all_nationalities', dataType: 'json', type: 'GET' }),
    ]).then(([userRes, gendersRes, nationalitiesRes]) => {
      const user = JSON.parse(userRes.data.current_user);
      this.setState({
        user: {
          bio: user.bio || '',
          nationality: user.nationality || '',
          gender_id: user.gender_id || null,
          birthday: user.birthday ? new Date(user.birthday) : null,
          // Keep constructor default (false = worldwide); DB default is true before this step
          local_chats_only: this.state.user.local_chats_only,
          first_name: user.first_name || '',
        },
        allGenders: JSON.parse(gendersRes.data.genders),
        allNationalities: JSON.parse(nationalitiesRes.data.nationalities),
        loaded: true,
      });
    }).catch((err) => {
      console.log(err);
    });
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
  }

  updateUser(value, attribute) {
    this.setState((prev) => ({
      user: { ...prev.user, [attribute]: value },
      errors: { ...prev.errors, [attribute]: '' },
    }));
  }

  updateBirthday(value) {
    this.updateUser(value, 'birthday');
  }

  validate() {
    const errors = {};
    if (!this.state.user.gender_id) {
      errors.gender_id = 'Please select your gender.';
    }
    if (!this.state.user.birthday) {
      errors.birthday = 'Please enter your birthday.';
    } else if (!isAtLeastMinAge(this.state.user.birthday)) {
      errors.birthday = 'You must be at least 18 years old.';
    }
    this.setState({ errors });
    if (Object.keys(errors).length > 0) {
      this.showOnboardingToast(errors.gender_id || errors.birthday);
    }
    return Object.keys(errors).length === 0;
  }

  componentWillUnmount() {
    this.clearOnboardingToast();
  }

  formatBirthday(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (!this.validate()) return;
    this.setState({ submitting: true });
    this.formRef.current.submit();
  }

  genderOptions() {
    return this.state.allGenders.map((gender) => ({
      value: gender.id,
      label: gender.name,
    }));
  }

  render() {
    if (!this.state.loaded) {
      return (
        <div className="onboarding-page-wrap">
          <div className="onboarding-page">
            <p className="onboarding-page__subtitle" style={{ paddingLeft: 0, textAlign: 'center' }}>Loading…</p>
          </div>
        </div>
      );
    }

    const { user, errors } = this.state;
    const aboutLabel = user.first_name
      ? `About ${user.first_name}`
      : 'About you';

    return (
      <div className="onboarding-page-wrap">
        <div className="onboarding-page">
          <header className="onboarding-page__header">
            <div className="onboarding-page__titleRow">
              <span className="onboarding-page__iconCircle" aria-hidden="true">
                <Icon name="user" size={22} color={semantic.primary.text.label} solid />
              </span>
              <h1 className="onboarding-page__title">Last step! Complete your profile</h1>
            </div>
            <p className="onboarding-page__subtitle">
              Tell partners a little about yourself
            </p>
          </header>

          <div className="lex-dash-card onboarding-page__section">
            <form
              ref={this.formRef}
              action="/update_user"
              method="post"
              className="onboarding-profile__form"
              onSubmit={this.handleSubmit}
            >
              <input type="hidden" name="authenticity_token" value={this.csrfToken()} />

              <SettingsLongTextInput
                label={aboutLabel}
                attribute="bio"
                value={user.bio}
                onChange={this.updateUser}
                placeholder={'Why are you studying a language?\nWhat do you want to work on?\n\nWhere are you from?'}
                icon="file-alt"
                tone="grey"
                rows={5}
              />
              <input type="hidden" name="user[bio]" value={user.bio || ''} />

              <DropdownSelect
                label="Nationality"
                attribute="nationality"
                value={user.nationality}
                onChange={this.updateUser}
                placeholder="Select nationality"
                items={this.state.allNationalities}
                itemAttribute="nationality"
                itemSelector="nationality"
                icon="building"
                tone="blue"
              />
              <input type="hidden" name="user[nationality]" value={user.nationality || ''} />

              <LexChipGroup
                label="Gender"
                options={this.genderOptions()}
                value={user.gender_id}
                attribute="gender_id"
                onChange={this.updateUser}
                tone="grey"
              />
              <input type="hidden" name="user[gender_id]" value={user.gender_id || ''} />
              {errors.gender_id && <p className="onboarding-page__error">{errors.gender_id}</p>}

              <DateInput
                onChange={this.updateBirthday}
                label="Year of birth"
                value={user.birthday}
                maxDate={getMaxBirthdayDate()}
                showYearDropdown
                showMonthDropdown
                tone="green"
                hint="You must be at least 18 years old"
                inputError={errors.birthday || ''}
              />
              <input type="hidden" name="user[birthday]" value={this.formatBirthday(user.birthday)} />

              <div className="lex-field">
                <p className="lex-field__label">Who can request to Connect with you?</p>
                <div className="onboarding-profile__connectOptions">
                  <SelectButton
                    morphism
                    buttonText="Only people in my location"
                    selected={user.local_chats_only === true}
                    onClick={() => this.updateUser(true, 'local_chats_only')}
                  />
                  <SelectButton
                    morphism
                    buttonText="Language matches around the world"
                    selected={user.local_chats_only === false}
                    onClick={() => this.updateUser(false, 'local_chats_only')}
                  />
                </div>
              </div>
              <input type="hidden" name="user[local_chats_only]" value={user.local_chats_only ? '1' : '0'} />

              <div className="onboarding-page__footer">
                <OnboardingToast message={this.state.toastMessage} />
                <StandardButton
                  text="Finish profile"
                  variant="primary"
                  fullWidth
                  size="large"
                  loading={this.state.submitting}
                  onClick={this.handleSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

export default SignUpCompleteProfile;
